import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  Brain,
  FileText,
  FolderOpen,
  Globe2,
  MessageSquare,
  Moon,
  PanelLeft,
  Plus,
  Search,
  Settings,
  Shapes,
  Sparkles,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { useUpdateSettings } from "../lib/settings";
import { useUI } from "../lib/ui-store";

interface Hit {
  kind: "chat" | "message" | "artifact" | "file" | "memory" | "project";
  id: string;
  title: string;
  snippet?: string;
  conversationId?: string;
}
interface Item {
  key: string;
  label: string;
  hint?: string;
  icon: ReactNode;
  run: () => void;
}

/** Ctrl/Cmd+K: quick actions + global search across chats, messages, artifacts, files, memories and projects. */
export function CommandPalette() {
  const t = useT();
  const navigate = useNavigate();
  const { theme, locale, agentMode, set, sidebarOpen } = usePrefs();
  const update = useUpdateSettings();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
    }
  }, [open]);

  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(id);
  }, [q]);
  const hits = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api<{ hits: Hit[] }>(`/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2,
  });

  const go = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  const actions: Item[] = [
    {
      key: "new",
      label: t("chat.new"),
      icon: <Plus className="size-4" />,
      run: go(() => navigate("/")),
    },
    {
      key: "agent",
      label: agentMode ? t("palette.agentOff") : t("palette.agentOn"),
      icon: <Bot className="size-4" />,
      run: go(() => set({ agentMode: !agentMode })),
    },
    {
      key: "sidebar",
      label: t("sidebar.toggle"),
      icon: <PanelLeft className="size-4" />,
      run: go(() => set({ sidebarOpen: !sidebarOpen })),
    },
    {
      key: "artifacts",
      label: t("nav.artifacts"),
      icon: <Shapes className="size-4" />,
      run: go(() => navigate("/artifacts")),
    },
    {
      key: "memory",
      label: t("settings.memory"),
      icon: <Brain className="size-4" />,
      run: go(() => navigate("/settings/memory")),
    },
    {
      key: "models",
      label: t("settings.models"),
      icon: <Settings className="size-4" />,
      run: go(() => navigate("/settings/models")),
    },
    {
      key: "agents",
      label: t("settings.agents"),
      icon: <Sparkles className="size-4" />,
      run: go(() => navigate("/settings/agents")),
    },
    {
      key: "usage",
      label: t("settings.usage"),
      icon: <Settings className="size-4" />,
      run: go(() => navigate("/settings/usage")),
    },
    {
      key: "settings",
      label: t("sidebar.settings"),
      icon: <Settings className="size-4" />,
      run: go(() => navigate("/settings")),
    },
    {
      key: "theme",
      label: t("palette.cycleTheme"),
      icon: <Moon className="size-4" />,
      run: go(() =>
        update.mutate({
          theme:
            theme === "dark"
              ? "black"
              : theme === "black"
                ? "light"
                : theme === "light"
                  ? "system"
                  : "dark",
        }),
      ),
    },
    {
      key: "lang",
      label: locale === "ar" ? "English" : "العربية",
      icon: <Globe2 className="size-4" />,
      run: go(() => update.mutate({ locale: locale === "ar" ? "en" : "ar" })),
    },
  ];
  const results: Item[] = (hits.data?.hits ?? []).map((h) => ({
    key: `${h.kind}:${h.id}`,
    label: h.title,
    hint: h.snippet?.replace(/[[\]]/g, ""),
    icon:
      h.kind === "memory" ? (
        <Brain className="size-4" />
      ) : h.kind === "project" ? (
        <FolderOpen className="size-4" />
      ) : h.kind === "file" ? (
        <FileText className="size-4" />
      ) : h.kind === "artifact" ? (
        <Shapes className="size-4" />
      ) : (
        <MessageSquare className="size-4" />
      ),
    run: go(() => {
      if (h.kind === "chat" || h.kind === "message") navigate(`/c/${h.conversationId ?? h.id}`);
      else if (h.kind === "artifact") {
        useUI.getState().openArtifact(h.id);
        navigate(h.conversationId ? `/c/${h.conversationId}` : "/artifacts");
      } else if (h.kind === "project") navigate(`/p/${h.id}`);
      else if (h.kind === "memory") navigate("/settings/memory");
      else window.open(`/api/files/${h.id}`, "_blank");
    }),
  }));
  const filtered = actions.filter(
    (a) => !q.trim() || a.label.toLowerCase().includes(q.trim().toLowerCase()),
  );
  const items = [...results, ...filtered];

  if (!open) return null;
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled on the dialog; the backdrop click is a pointer convenience
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop for the modal palette
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/55 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("palette.title")}
        className="glass w-full max-w-xl overflow-hidden rounded-2xl border border-[var(--line-strong)] shadow-[var(--shadow)]"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSel((s) => Math.min(items.length - 1, s + 1));
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setSel((s) => Math.max(0, s - 1));
          }
          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            items[sel]?.run();
          }
        }}
      >
        <div className="flex items-center gap-3 border-b border-[var(--line)] px-4">
          <Search className="size-4 text-[var(--fg-faint)]" />
          <input
            // biome-ignore lint/a11y/noAutofocus: a palette must take focus the moment it opens
            autoFocus
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            dir="auto"
            placeholder={t("palette.placeholder")}
            aria-label={t("palette.placeholder")}
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--fg-faint)]"
          />
          <kbd className="rounded border border-[var(--line)] px-1.5 py-0.5 text-[10px] text-[var(--fg-faint)]">
            Esc
          </kbd>
        </div>
        <ul className="max-h-[50dvh] overflow-y-auto p-1.5">
          {items.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-[var(--fg-faint)]">
              {hits.isFetching ? "…" : t("sidebar.noResults")}
            </li>
          )}
          {items.map((it, i) => (
            <li key={it.key}>
              <button
                type="button"
                onMouseEnter={() => setSel(i)}
                onClick={it.run}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm ${i === sel ? "bg-[var(--surface-3)]" : ""}`}
              >
                <span className="text-[var(--fg-muted)]">{it.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate" dir="auto">
                    {it.label}
                  </span>
                  {it.hint && (
                    <span className="block truncate text-xs text-[var(--fg-faint)]" dir="auto">
                      {it.hint}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
